import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth, useSettings } from '../../App';
import { authAPI } from '../../services/api';
import { Button, Field, Input, Card } from '../../ui';
import './Login.css';

// Login — v2.8.0 redesign.
//
// Two screens, same component:
//   1. Role chooser (no role selected) — admin/manager vs worker, two
//      large cards. Stripped of emoji decoration; icon set is inline SVG.
//   2. Credentials form — email + password, branded primary CTA, inline
//      error/flash banners.
//
// Flash banner behavior unchanged from v2.7.8: reads sessionStorage
// `flash_auth`, displays once, clears.

function ShieldIcon() {
  return (
    <svg viewBox="0 0 24 24" width="22" height="22" fill="none" aria-hidden="true">
      <path d="M12 2.5l8 3.5v6c0 4.5-3.4 8.6-8 9.5-4.6-.9-8-5-8-9.5V6l8-3.5z"
            stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
      <path d="M9.5 12l1.7 1.8 3.3-3.6" stroke="currentColor" strokeWidth="1.5"
            strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function ToolIcon() {
  return (
    <svg viewBox="0 0 24 24" width="22" height="22" fill="none" aria-hidden="true">
      <path d="M14 7.5a3.5 3.5 0 0 1 4.5-3.4l-2.4 2.4 1.4 1.4 2.4-2.4A3.5 3.5 0 0 1 16.5 10l-7 7a2.1 2.1 0 1 1-3-3l7-7a3.5 3.5 0 0 1 .5-2.5"
            stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function ChevronRight() {
  return (
    <svg viewBox="0 0 16 16" width="14" height="14" fill="none" aria-hidden="true">
      <path d="M6 4l4 4-4 4" stroke="currentColor" strokeWidth="1.5"
            strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function EyeIcon({ off }) {
  if (off) {
    return (
      <svg viewBox="0 0 20 20" width="18" height="18" fill="none" aria-hidden="true">
        <path d="M3 3l14 14M7 6.5A8.6 8.6 0 0 0 2 10s3 5 8 5a7.5 7.5 0 0 0 3.5-.9M11 6.2a8.7 8.7 0 0 1 7 3.8s-1 1.7-2.8 3.2"
              stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 20 20" width="18" height="18" fill="none" aria-hidden="true">
      <path d="M2 10s3-5 8-5 8 5 8 5-3 5-8 5-8-5-8-5z"
            stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
      <circle cx="10" cy="10" r="2.5" stroke="currentColor" strokeWidth="1.5" />
    </svg>
  );
}

function Login() {
  const [selectedRole, setSelectedRole] = useState(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [flashInfo, setFlashInfo] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const { settings } = useSettings();
  const navigate = useNavigate();

  useEffect(() => {
    try {
      const msg = sessionStorage.getItem('flash_auth');
      if (msg) {
        setFlashInfo(msg);
        sessionStorage.removeItem('flash_auth');
      }
    } catch (_) { /* sessionStorage may be unavailable */ }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const { data } = await authAPI.login({ email, password });
      login(data.token, data.user);
      navigate('/');
    } catch (err) {
      setError(err.response?.data?.error || 'Login failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    setSelectedRole(null);
    setEmail('');
    setPassword('');
    setError('');
  };

  const brandName = settings?.brand_name || 'Full Circle Property';

  // ── Role chooser ───────────────────────────────────────────────────
  if (!selectedRole) {
    return (
      <div className="login-page">
        <div className="login-stage">
          <header className="login-mark">
            {settings?.brand_logo ? (
              <img src={settings.brand_logo} alt={brandName} className="login-mark__logo" />
            ) : (
              <span className="login-mark__short">{settings?.brand_short || 'FCP'}</span>
            )}
            <span className="login-mark__divider" aria-hidden="true" />
            <span className="login-mark__name">FieldWorks</span>
          </header>

          <div className="login-content">
            <h1 className="login-title">Welcome back.</h1>
            <p className="login-subtitle">Sign in to continue.</p>

            <div className="login-roles">
              <button type="button"
                      className="login-role"
                      onClick={() => setSelectedRole('admin')}>
                <span className="login-role__icon"><ShieldIcon /></span>
                <span className="login-role__body">
                  <span className="login-role__title">Admin or manager</span>
                  <span className="login-role__hint">Workers, jobs, billing, reports</span>
                </span>
                <span className="login-role__chev" aria-hidden="true"><ChevronRight /></span>
              </button>

              <button type="button"
                      className="login-role"
                      onClick={() => setSelectedRole('worker')}>
                <span className="login-role__icon"><ToolIcon /></span>
                <span className="login-role__body">
                  <span className="login-role__title">Worker</span>
                  <span className="login-role__hint">Clock in, jobs, timesheets</span>
                </span>
                <span className="login-role__chev" aria-hidden="true"><ChevronRight /></span>
              </button>
            </div>

            <p className="login-foot">
              First time here? <Link to="/register" className="login-link">Create an account</Link>.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // ── Credentials form ───────────────────────────────────────────────
  return (
    <div className="login-page">
      <div className="login-stage">
        <header className="login-mark">
          {settings?.brand_logo ? (
            <img src={settings.brand_logo} alt={brandName} className="login-mark__logo" />
          ) : (
            <span className="login-mark__short">{settings?.brand_short || 'FCP'}</span>
          )}
          <span className="login-mark__divider" aria-hidden="true" />
          <span className="login-mark__name">FieldWorks</span>
        </header>

        <div className="login-content">
          <button type="button" className="login-back" onClick={handleBack}>
            ← Back
          </button>

          <h1 className="login-title">Sign in</h1>
          <p className="login-subtitle">
            {selectedRole === 'admin' ? 'Admin or manager.' : 'Worker.'}
          </p>

          {flashInfo && !error && (
            <Card padding="sm" className="login-banner login-banner--info">
              {flashInfo}
            </Card>
          )}
          {error && (
            <Card padding="sm" className="login-banner login-banner--error" role="alert">
              {error}
            </Card>
          )}

          <form onSubmit={handleSubmit} className="login-form">
            <Field label="Email" required htmlFor="login-email">
              <Input
                id="login-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                autoComplete="email"
                autoFocus
                required
                size="lg"
              />
            </Field>

            <Field label="Password" required htmlFor="login-password">
              <div className="login-password">
                <Input
                  id="login-password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  autoComplete="current-password"
                  required
                  size="lg"
                />
                <button
                  type="button"
                  className="login-password__toggle"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  tabIndex={-1}
                >
                  <EyeIcon off={showPassword} />
                </button>
              </div>
            </Field>

            <Button
              type="submit"
              variant="primary"
              size="lg"
              fullWidth
              loading={loading}
              className="login-submit"
            >
              {loading ? 'Signing in…' : 'Sign in'}
            </Button>

            <p className="login-foot">
              Don't have an account? <Link to="/register" className="login-link">Sign up as a worker</Link>.
            </p>
          </form>
        </div>
      </div>
    </div>
  );
}

export default Login;
